import { Grid } from "@mui/material";
import Fade from "@mui/material/Fade";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getHomepageAds,
  getHomepageLogo,
  getTrendingChip,
} from "../../../redux/actions/job_action";
import HomepageDropResume from "./HomepageDropResume/HomepageDropResume";
import HomepageHeader from "./HomepageHeader/HomepageHeader";
import HomepageLogo from "./HomepageLogo/HomepageLogo";
import HomepageMobileLogo from "./HomepageMobileLogo/HomepageMobileLogo";
import HomepageSearchBar from "./HomepageSearchBar/HomepageSearchBar";
import {
  ArrowIcon,
  BlankContainer,
  DesktopContainer,
  JWCContainer,
  LogoContainer,
  MobileContainer,
  MobileLogoContainer,
  OuterContainer,
  ResumeMsgTypography,
  RightArrowIcon,
  SearchContainer,
  SearchInner,
  TabletImageContainer,
  WPTContainer,
  WPTTypography,
} from "./styles";

/** Marquee grid is 9 tiles: 7 featured companies (BE) + 2 defaults (WPT + JWC, never in GraphQL). */
const HOMEPAGE_FEATURED_COMPANY_LOGO_COUNT = 7;
const DESKTOP_LEFT_COMPANY_COUNT = 3;
const DESKTOP_RIGHT_COMPANY_COUNT = 4;
const MOBILE_COMPANY_INDEX_BY_CELL = [0, null, 1, 2, null, 3, 4, 5, 6];

export default function HomepageSearch(props) {
  const { userResume, setUserResume, inputFileRef } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const homepageLogo = useSelector((state) => state?.jobs?.homepageLogo);

  const companyLogoEdges = Array.isArray(homepageLogo) ? homepageLogo : [];
  const featuredCompanyEdges = companyLogoEdges.slice(
    0,
    HOMEPAGE_FEATURED_COMPANY_LOGO_COUNT
  );
  // Desktop arrangement only: left [0,1,WPT,2], right [JWC,3,4,5,6].
  const firstHomepageLogo = featuredCompanyEdges.slice(0, DESKTOP_LEFT_COMPANY_COUNT);
  const secondHomepageLogo = featuredCompanyEdges.slice(
    DESKTOP_LEFT_COMPANY_COUNT,
    DESKTOP_LEFT_COMPANY_COUNT + DESKTOP_RIGHT_COMPANY_COUNT
  );

  const trendingChip = useSelector((state) => state?.jobs?.trendingChip);

  useEffect(() => {
    dispatch(getHomepageLogo());
    dispatch(getTrendingChip());
    dispatch(getHomepageAds());
  }, []);

  // Pulsing sequence

  const screenDesktop = useMediaQuery("(min-width:1280px)");
  const screenTablet = useMediaQuery("(min-width:768px)");

  const imagePlaceholder = [
    "/images/homepage-logo-placeholder/icon_internship.webp",
    "/images/homepage-logo-placeholder/icon_court.webp",
    "/images/homepage-logo-placeholder/icon_shop.webp",
    "/images/homepage-logo-placeholder/icon_puzzle.webp",
    "/images/homepage-logo-placeholder/icon_hiredly_logo.webp",
    "/images/homepage-logo-placeholder/icon_code.webp",
    "/images/homepage-logo-placeholder/icon_fire.webp",
    "/images/homepage-logo-placeholder/icon_company.webp",
    "/images/homepage-logo-placeholder/icon_medical.webp",
  ];

  const workPersonaTest = [
    {
      title: "Hiredly's Work Personality Test",
      url: "https://hiredly.typeform.com/to/K4idZTDK?utm_medium=shortened_link&utm_source=test&utm_campaign=work_persona_2",
    },
  ];

  const jobWorthCalculator = [
    {
      title: "Hiredly's Job Worth Calculator",
      url: "/worthy",
    },
  ];

  const JWCTextRaw = t("homepage.job_worth_calculator_text");
  const JWCText = <span dangerouslySetInnerHTML={{ __html: JWCTextRaw }} />;

  const WPTTextRaw = t("homepage.work_persona_test_text");
  // Parse the text to handle HTML tags
  const WPTText = <span dangerouslySetInnerHTML={{ __html: WPTTextRaw }} />;

  return (
    <Fragment>
      {screenDesktop ? (
        <OuterContainer container justifyContent="center" alignItems="center">
          <Fade in={true} timeout={400}>
            <DesktopContainer
              container
              direction="row"
              columnGap="50px"
              flexWrap="nowrap"
              alignItems="center"
            >
              {featuredCompanyEdges.length > 0 && (
                <>
                  <WPTContainer>
                    <WPTTypography>{WPTText}</WPTTypography>
                  </WPTContainer>
                  <ArrowIcon />
                </>
              )}
              <LogoContainer
                container
                item
                xs={3}
                lg={3}
                direction="row"
                isFirst
                columnGap="20px"
              >
                <HomepageLogo
                  workPersonaTest={workPersonaTest[0]}
                  jobWorthCalculator={jobWorthCalculator[0]}
                  imageList={firstHomepageLogo}
                />
              </LogoContainer>

              {/* For custom cursor when hovering onto company logo */}
              <SearchContainer
                // border={"1px solid red"}
                display={"flex"}
                flexDirection={"column"}
                justifyContent="center"
                alignItems="center"
                alignContent="center"
                direction="column"
                item
                xs={6}
                lg={5.1}
              >
                <SearchInner>
                  <HomepageHeader />
                  <HomepageSearchBar />
                  <HomepageDropResume
                    inputFileRef={inputFileRef}
                    triggerToast={props.triggerToast}
                    userResume={userResume}
                    setUserResume={setUserResume}
                  />
                  {/* <HomepageTrending /> */}
                </SearchInner>
                <ResumeMsgTypography>
                  {t("homepage.resume_message")}
                </ResumeMsgTypography>
              </SearchContainer>

              {/* Show second logo container if in desktop view */}
              {screenDesktop && (
                <>
                  {featuredCompanyEdges.length > 0 && (
                    <>
                      <JWCContainer>
                        <WPTTypography>{JWCText}</WPTTypography>
                      </JWCContainer>
                      <RightArrowIcon />
                    </>
                  )}
                  <LogoContainer
                    container
                    isSecond
                    item
                    xs={3}
                    lg={3}
                    columnGap="20px"
                  >
                    {/* Search Bar */}
                    <HomepageLogo
                      isSecond
                      imageList={secondHomepageLogo}
                      jobWorthCalculator={jobWorthCalculator[0]}
                    />
                  </LogoContainer>
                </>
              )}
            </DesktopContainer>
          </Fade>
        </OuterContainer>
      ) : (
        <Fade in={true} timeout={400}>
          <MobileContainer
            container
            direction="column"
            sx={{
              minHeight:
                Array.isArray(homepageLogo) &&
                homepageLogo.length < 1 &&
                "100% !important",

              paddingTop:
                Array.isArray(homepageLogo) &&
                homepageLogo.length < 1 &&
                "50px !important",
            }}
          >
            {screenTablet ? (
              <TabletImageContainer>
                <LogoContainer
                  container
                  item
                  direction="row"
                  columnGap="20px"
                  xs={12}
                >
                  {featuredCompanyEdges.length > 0 && (
                    <>
                      <WPTContainer>
                        <WPTTypography>{WPTText}</WPTTypography>
                      </WPTContainer>
                      <ArrowIcon />
                    </>
                  )}
                  {featuredCompanyEdges.length > 0 && (
                    <>
                      <JWCContainer>
                        <WPTTypography>{JWCText}</WPTTypography>
                      </JWCContainer>
                      <RightArrowIcon />
                    </>
                  )}
                  <HomepageLogo
                    imageList={featuredCompanyEdges}
                    imagePlaceholder={imagePlaceholder}
                    screenTablet={screenTablet}
                    workPersonaTest={workPersonaTest[0]}
                    jobWorthCalculator={jobWorthCalculator[0]}
                  />
                </LogoContainer>
              </TabletImageContainer>
            ) : (
              <BlankContainer container>
                <MobileLogoContainer
                  margin="auto"
                  justifyContent="center"
                  alignItems="center"
                  display={"grid"}
                  gap="8px"
                  gridTemplateColumns="repeat(3, auto)"
                  xs={12}
                >
                  {Array.from({ length: 9 }).map((_, index) => {
                    const companyIndex = MOBILE_COMPANY_INDEX_BY_CELL[index];
                    return (
                      <HomepageMobileLogo
                        key={index}
                        id={index}
                        imageList={
                          companyIndex === null
                            ? undefined
                            : featuredCompanyEdges[companyIndex]
                        }
                        imagePlaceholder={imagePlaceholder}
                        workPersonaTest={index === 4 ? workPersonaTest[0] : undefined}
                        jobWorthCalculator={index === 1 ? jobWorthCalculator[0] : undefined}
                      />
                    );
                  })}
                </MobileLogoContainer>
              </BlankContainer>
            )}

            <SearchContainer container item>
              <Grid height={"100%"} maxWidth="520px">
                <Grid>
                  <HomepageHeader />
                  {/* <HomepageAnimatedHeader /> */}
                  <HomepageSearchBar />
                </Grid>
                <HomepageDropResume
                  inputFileRef={inputFileRef}
                  triggerToast={props.triggerToast}
                  userResume={userResume}
                  setUserResume={setUserResume}
                />
                <ResumeMsgTypography>
                  {t("homepage.resume_message")}
                </ResumeMsgTypography>
              </Grid>
              {/* <HomepageTrending /> */}
            </SearchContainer>
          </MobileContainer>
        </Fade>
      )}
    </Fragment>
  );
}
